import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, CheckCircle2, XCircle, FileText, Brush } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const PERIOD_OPTIONS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

const BY_TYPE_LABELS = {
  next_actions: "Next actions",
  outcome_suggestion: "Outcome suggestions",
  compliance_checklist: "Compliance checklist",
};

function formatPercent(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${Math.round(value * 1000) / 10}%`;
}

function StatCard({ label, value, sublabel, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-xl px-4 py-3"
      style={{
        backgroundColor: "var(--surface-2)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--page-fg-muted)" }}
          >
            {label}
          </p>
          <p
            className="mt-1 font-serif text-2xl font-semibold tabular-nums"
            style={{ color: "var(--page-fg)", letterSpacing: "-0.02em" }}
            data-testid={`${testid}-value`}
          >
            {value}
          </p>
          {sublabel && (
            <p
              className="mt-1 text-[11px]"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              {sublabel}
            </p>
          )}
        </div>
        {Icon && (
          <div
            aria-hidden
            className="flex h-7 w-7 items-center justify-center rounded-md"
            style={{
              backgroundColor: accent?.bg || "var(--surface)",
              color: accent?.fg || "var(--page-fg-muted)",
              border: `1px solid ${accent?.border || "var(--border-subtle)"}`,
            }}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
          </div>
        )}
      </div>
    </div>
  );
}

function BarRow({ label, applied, dismissed, rate }) {
  const total = applied + dismissed;
  const appliedPct = total > 0 ? (applied / total) * 100 : 0;
  return (
    <div
      data-testid={`copilot-by-type-${label
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
      className="space-y-1"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span
          className="text-xs font-medium"
          style={{ color: "var(--page-fg)" }}
        >
          {label}
        </span>
        <span
          className="text-[11px] tabular-nums"
          style={{ color: "var(--page-fg-secondary)" }}
        >
          <span style={{ color: "var(--success-fg)" }}>{applied}</span>
          <span style={{ color: "var(--page-fg-muted)" }}> applied · </span>
          <span style={{ color: "var(--danger-fg)" }}>{dismissed}</span>
          <span style={{ color: "var(--page-fg-muted)" }}> dismissed</span>
          <span
            className="ml-2 inline-block min-w-[44px] text-right font-semibold"
            style={{ color: "var(--page-fg)" }}
          >
            {formatPercent(rate)}
          </span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={Math.round(appliedPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} apply ratio`}
        className="h-1.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: "var(--surface)" }}
      >
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{
            width: `${appliedPct}%`,
            backgroundColor: "var(--success-fg)",
          }}
        />
      </div>
    </div>
  );
}

export default function CopilotEffectivenessTile() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/insights/copilot-effectiveness?days=${days}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
        setError(detail);
        if (err.response?.status !== 403) {
          toast.error(detail);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const byTypeRows = useMemo(() => {
    if (!data?.by_type) return [];
    return Object.entries(data.by_type).map(([key, val]) => ({
      key,
      label: BY_TYPE_LABELS[key] || key,
      applied: val.applied || 0,
      dismissed: val.dismissed || 0,
      rate: val.rate || 0,
    }));
  }, [data]);

  if (error && error.includes("not permitted")) {
    return null; // Hide tile entirely for roles without access
  }

  return (
    <div
      data-testid="copilot-effectiveness-tile"
      className="brand-card p-6"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
              color: "var(--warning-fg)",
            }}
          >
            <Sparkles className="h-4 w-4" strokeWidth={1.7} />
          </div>
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: "var(--page-fg)", letterSpacing: "-0.005em" }}
            >
              Co-pilot effectiveness
              <span
                className="ml-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: "var(--warning-bg)",
                  color: "var(--warning-fg)",
                  boxShadow: "inset 0 0 0 1px var(--warning-border)",
                }}
              >
                AI
              </span>
            </h3>
            <p
              className="mt-0.5 text-xs"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              How HR is leveraging AI suggestions across cases.
            </p>
          </div>
        </div>

        <div
          role="radiogroup"
          aria-label="Lookback window"
          className="inline-flex items-center gap-1 rounded-lg p-1"
          style={{
            backgroundColor: "var(--surface-2)",
            border: "1px solid var(--border-subtle)",
          }}
          data-testid="copilot-effectiveness-period-selector"
        >
          {PERIOD_OPTIONS.map((opt) => {
            const active = days === opt.value;
            return (
              <button
                key={opt.value}
                role="radio"
                aria-checked={active}
                type="button"
                onClick={() => setDays(opt.value)}
                disabled={loading}
                data-testid={`copilot-effectiveness-period-${opt.value}`}
                className={
                  "rounded-md px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] transition-colors " +
                  (active
                    ? "shadow-sm"
                    : "hover:bg-[color:var(--surface)]")
                }
                style={
                  active
                    ? {
                        backgroundColor: "var(--surface)",
                        color: "var(--page-fg)",
                        border: "1px solid var(--border-subtle)",
                      }
                    : {
                        color: "var(--page-fg-muted)",
                        border: "1px solid transparent",
                      }
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && !data ? (
        <div
          className="h-44 animate-pulse rounded-xl"
          style={{ backgroundColor: "var(--surface-2)" }}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              testid="copilot-stat-generated"
              label="Suggestions generated"
              value={data?.suggestions_generated ?? 0}
              sublabel={`Last ${days} days`}
              icon={Sparkles}
              accent={{
                bg: "var(--warning-bg)",
                fg: "var(--warning-fg)",
                border: "var(--warning-border)",
              }}
            />
            <StatCard
              testid="copilot-stat-apply-rate"
              label="Apply rate"
              value={formatPercent(data?.apply_rate)}
              sublabel={`${data?.suggestions_applied ?? 0} applied`}
              icon={CheckCircle2}
              accent={{
                bg: "var(--success-bg)",
                fg: "var(--success-fg)",
                border: "var(--success-border)",
              }}
            />
            <StatCard
              testid="copilot-stat-dismiss-rate"
              label="Dismiss rate"
              value={formatPercent(data?.dismiss_rate)}
              sublabel={`${data?.suggestions_dismissed ?? 0} dismissed`}
              icon={XCircle}
              accent={{
                bg: "var(--danger-bg)",
                fg: "var(--danger-fg)",
                border: "var(--danger-border)",
              }}
            />
            <StatCard
              testid="copilot-stat-closure-drafts"
              label="Closure drafts used"
              value={data?.closure_drafts_used ?? 0}
              sublabel={`Redactions applied: ${data?.redactions_applied ?? 0}`}
              icon={FileText}
              accent={{
                bg: "var(--surface)",
                fg: "var(--page-fg)",
                border: "var(--border-subtle)",
              }}
            />
          </div>

          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2">
              <Brush
                className="h-3.5 w-3.5"
                strokeWidth={1.7}
                style={{ color: "var(--page-fg-muted)" }}
              />
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                By suggestion type
              </p>
            </div>

            {byTypeRows.length === 0 ||
            byTypeRows.every((r) => r.applied + r.dismissed === 0) ? (
              <p
                className="rounded-xl border border-dashed px-3 py-4 text-center text-[11px]"
                style={{
                  borderColor: "var(--border-subtle)",
                  color: "var(--page-fg-muted)",
                  backgroundColor: "var(--surface-2)",
                }}
              >
                No co-pilot decisions in this window.
              </p>
            ) : (
              <div className="space-y-3">
                {byTypeRows.map((row) => (
                  <BarRow
                    key={row.key}
                    label={row.label}
                    applied={row.applied}
                    dismissed={row.dismissed}
                    rate={row.rate}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <p
        className="mt-4 text-[10px] font-medium uppercase tracking-[0.14em]"
        style={{ color: "var(--page-fg-muted)" }}
      >
        AI insights · advisory · review by HR before action
      </p>
    </div>
  );
}
